import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import { AuthContext } from '../../App';

/**
 * Account → Security section for two-factor authentication management.
 *
 * MFA is mandatory on main-app accounts (HIPAA §164.312(d)), so this
 * component never offers "Disable 2FA" — only method changes + backup
 * code regeneration. "Change method" and "Regenerate backup codes"
 * require fresh MFA verification (session.mfa_verified_at ≤ 5 min).
 * If expired, we ask the user to re-authenticate.
 */
function AccountSecurityMFA() {
  const { token } = React.useContext(AuthContext);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showChangeMethod, setShowChangeMethod] = useState(false);
  const [newMethod, setNewMethod] = useState('');
  const [changeBusy, setChangeBusy] = useState(false);

  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState(null);
  const [regenBusy, setRegenBusy] = useState(false);

  const [needsReauth, setNeedsReauth] = useState(false);

  const bearer = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  });

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/api/auth/mfa/status`, { headers: bearer() });
      if (resp.ok) setStatus(await resp.json());
    } catch (e) {
      setError('Failed to load 2FA status');
    }
    setLoading(false);
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleStartChangeMethod = async () => {
    setChangeBusy(true);
    setError('');
    try {
      const resp = await fetch(`${API_BASE}/api/auth/mfa/change-method`, {
        method: 'POST',
        headers: bearer(),
        body: JSON.stringify({ new_method: newMethod }),
      });
      if (resp.status === 403) {
        setNeedsReauth(true);
      } else if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setError(data.detail || 'Change failed');
      } else {
        // Backend returned { message, temp_token, expires_in }. For MVP we
        // tell the user to log out + in to finish enrolling the new method.
        // (Phase 2.5 can pipe them through a setup flow without re-login.)
        alert(
          'Your current 2FA method has been cleared. Please log out and log back in — ' +
          'you will be prompted to set up the new method on your next login.'
        );
        setShowChangeMethod(false);
        setNewMethod('');
        fetchStatus();
      }
    } catch (e) {
      setError('Network error');
    }
    setChangeBusy(false);
  };

  const handleRegenerateBackupCodes = async () => {
    setRegenBusy(true);
    setError('');
    try {
      const resp = await fetch(`${API_BASE}/api/auth/mfa/regenerate-backup-codes`, {
        method: 'POST',
        headers: bearer(),
      });
      if (resp.status === 403) {
        setNeedsReauth(true);
      } else if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setError(data.detail || 'Regenerate failed');
      } else {
        const data = await resp.json();
        setNewBackupCodes(data.backup_codes || []);
        setShowRegenerateConfirm(false);
        fetchStatus();
      }
    } catch (e) {
      setError('Network error');
    }
    setRegenBusy(false);
  };

  if (loading) return <div>Loading 2FA status...</div>;
  if (!status) return null;

  const methodLabel = {
    totp: 'Authenticator App',
    sms: 'Text Message (SMS)',
    email: 'Email',
  };

  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <h3 style={{ marginTop: 0, marginBottom: 12 }}>Two-Factor Authentication</h3>

      {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

      {needsReauth && (
        <div className="alert alert-warning" style={{ marginBottom: 12 }}>
          For security, this action requires a recent 2FA verification. Please log out and log back in to continue.
          <button className="btn btn-secondary btn-sm" style={{ marginLeft: 8 }} onClick={() => setNeedsReauth(false)}>
            Dismiss
          </button>
        </div>
      )}

      {status.enabled ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <strong>Status:</strong>{' '}
              <span style={{ color: 'var(--color-success, #059669)' }}>Active</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--brand-text-muted, #6B7280)' }}>
              Method: <strong>{methodLabel[status.method] || status.method}</strong>
            </div>
          </div>

          <div style={{ marginBottom: 8, fontSize: 14 }}>
            <strong>Backup codes remaining:</strong> {status.backup_codes_remaining}
            {status.backup_codes_remaining <= 3 && (
              <span style={{ color: 'var(--color-error, #DC2626)', marginLeft: 8 }}>
                (Low — consider regenerating)
              </span>
            )}
          </div>

          <p style={{ fontSize: 13, color: 'var(--brand-text-muted, #6B7280)', marginBottom: 16 }}>
            Two-factor authentication is required for your account and cannot be disabled.
            You can change your method or regenerate your backup codes below.
          </p>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => { setShowChangeMethod(true); setError(''); setNeedsReauth(false); }}
            >
              Change Method
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => { setShowRegenerateConfirm(true); setError(''); setNeedsReauth(false); }}
            >
              Regenerate Backup Codes
            </button>
          </div>
        </>
      ) : (
        <div className="alert alert-warning">
          Two-factor authentication is not yet enabled. Please log out and log back in to complete setup — it is required to continue using your account.
        </div>
      )}

      {/* Change Method Modal */}
      {showChangeMethod && (
        <div className="modal-overlay" onClick={() => !changeBusy && setShowChangeMethod(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Change 2FA Method</h3>
            <p style={{ fontSize: 14, marginBottom: 16 }}>
              Your current method ({methodLabel[status.method]}) will be removed and you'll need to set up the new method.
            </p>
            <div className="form-group">
              <label>New method</label>
              <select value={newMethod} onChange={(e) => setNewMethod(e.target.value)}>
                <option value="">-- select --</option>
                {status.method !== 'totp' && <option value="totp">Authenticator App</option>}
                {status.method !== 'sms' && <option value="sms">Text Message (SMS)</option>}
                {status.method !== 'email' && <option value="email">Email</option>}
              </select>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" disabled={!newMethod || changeBusy} onClick={handleStartChangeMethod}>
                {changeBusy ? 'Working...' : 'Continue'}
              </button>
              <button type="button" className="btn btn-secondary" disabled={changeBusy} onClick={() => setShowChangeMethod(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Backup Codes — confirm modal */}
      {showRegenerateConfirm && (
        <div className="modal-overlay" onClick={() => !regenBusy && setShowRegenerateConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Regenerate Backup Codes</h3>
            <p style={{ fontSize: 14, marginBottom: 16 }}>
              Your existing backup codes will become invalid. You'll receive 10 new codes — save them somewhere safe.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" disabled={regenBusy} onClick={handleRegenerateBackupCodes}>
                {regenBusy ? 'Generating...' : 'Regenerate'}
              </button>
              <button type="button" className="btn btn-secondary" disabled={regenBusy} onClick={() => setShowRegenerateConfirm(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Show new backup codes once */}
      {newBackupCodes && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 className="modal-title">Your New Backup Codes</h3>
            <p style={{ fontSize: 14, color: 'var(--color-error, #DC2626)', fontWeight: 600, marginBottom: 12 }}>
              Save these codes now — they won't be shown again.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 16 }}>
              {newBackupCodes.map((code, i) => (
                <code key={i} style={{ padding: 8, background: '#f5f5f5', borderRadius: 4, textAlign: 'center', fontSize: 14 }}>
                  {code}
                </code>
              ))}
            </div>
            <button type="button" className="btn btn-primary" onClick={() => setNewBackupCodes(null)}>
              I've saved my codes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountSecurityMFA;
